import React from "react";
import { Helmet } from "react-helmet";
import Header from "../Header";
import Footer from "../Footer";
import "./CrossPlatformApps.css";

export default function CrossPlatformApps() {
    const services = [
        {
            title: "Cross-Platform Game Apps",
            description: "Build companion apps, casual game apps, community apps, and game product experiences for Android and iOS from one shared delivery plan.",
            points: ["Android & iOS", "Shared product flow", "Store-ready builds"]
        },
        {
            title: "Flutter Game Companion Apps",
            description: "Create fast companion apps for player accounts, wallets, rewards, leaderboards, tournaments, news, community, and live events.",
            points: ["Player profiles", "Rewards & wallets", "Live events"]
        },
        {
            title: "React Native Product Apps",
            description: "Develop scalable mobile apps for game platforms, esports products, fantasy contests, content hubs, and social gaming products.",
            points: ["Reusable modules", "API integration", "Fast iteration"]
        },
        {
            title: "App Upgrade & Porting",
            description: "Move existing apps to a cross-platform stack, refresh UI, improve performance, connect new APIs, and prepare stable releases.",
            points: ["Codebase migration", "UI refresh", "Performance tuning"]
        }
    ];

    const gamingModules = [
        { title: "Player Accounts", description: "Login, profiles, avatars, social IDs, permissions, and secure session handling for game users." },
        { title: "Leaderboards", description: "Rankings, scores, seasons, player stats, achievements, and competition tracking." },
        { title: "Wallet & Rewards", description: "Coins, gems, bonus systems, purchase history, reward claims, referrals, and transaction flows." },
        { title: "Tournament Flow", description: "Contest creation, entry fees, brackets, match schedules, winners, and results management." },
        { title: "LiveOps Content", description: "News, events, offers, banners, missions, push campaigns, and remote configuration." },
        { title: "Community Features", description: "Friends, teams, chat-ready flows, social sharing, notifications, and engagement loops." }
    ];

    const technologies = [
        "Flutter",
        "React Native",
        "Firebase",
        "REST APIs",
        "Node.js Backend",
        "Push Notifications",
        "Payments",
        "Wallet Systems",
        "Analytics",
        "Crash Reporting",
        "Admin Panel",
        "Cloud Deployment",
        "Android Builds",
        "iOS Builds",
        "Store Publishing"
    ];

    const appTypes = [
        "Game Companion Apps",
        "Fantasy Sports Apps",
        "Esports Apps",
        "Tournament Apps",
        "Game Community Apps",
        "Reward Apps",
        "Casual Game Apps",
        "Gaming Content Apps",
        "LiveOps Apps",
        "Game Store Apps"
    ];

    const process = [
        { step: "01", title: "Product Strategy", description: "We define target users, game ecosystem, platform needs, feature modules, backend scope, monetization, and launch goals." },
        { step: "02", title: "UX & Architecture", description: "We map player journeys, app screens, reusable components, API contracts, data flows, and admin requirements." },
        { step: "03", title: "Cross-Platform Build", description: "Our team builds shared app modules, Android/iOS builds, backend integrations, notifications, payments, and analytics." },
        { step: "04", title: "QA & Release Prep", description: "We test devices, OS versions, app performance, crashes, user flows, payments, notifications, and store readiness." },
        { step: "05", title: "Launch & LiveOps", description: "We support publishing, updates, event content, bug fixes, analytics review, feature releases, and ongoing maintenance." }
    ];

    const benefits = [
        { icon: "fa-solid fa-gamepad", title: "Gaming Product Focus", description: "We design cross-platform apps around player engagement, rewards, competition, community, and retention." },
        { icon: "fa-solid fa-code-branch", title: "One Delivery Plan", description: "A shared codebase helps reduce duplicate effort while still preparing polished Android and iOS releases." },
        { icon: "fa-solid fa-server", title: "Backend Ready", description: "We connect player accounts, wallets, tournaments, leaderboards, admin panels, analytics, and cloud services." },
        { icon: "fa-solid fa-rocket", title: "Launch & LiveOps Support", description: "We help with store publishing, updates, push campaigns, content events, monitoring, and product improvements." }
    ];

    const faqs = [
        {
            question: "Can cross-platform apps work for gaming products?",
            answer: "Yes. Cross-platform apps are a strong fit for game companion apps, fantasy sports apps, esports apps, tournament apps, reward systems, player communities, and gaming content platforms."
        },
        {
            question: "Do you use Flutter or React Native?",
            answer: "Yes. We can use Flutter or React Native depending on performance needs, UI complexity, existing codebase, backend integrations, and long-term maintenance goals."
        },
        {
            question: "Can you build backend features for game apps?",
            answer: "Yes. We build APIs, player accounts, leaderboards, wallets, tournaments, admin panels, analytics, notifications, and cloud deployment."
        },
        {
            question: "Can you publish both Android and iOS apps?",
            answer: "Yes. We help prepare Android and iOS builds, store assets, release notes, testing, compliance checks, and publishing support."
        }
    ];

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Cross-Platform App Development for Gaming Products",
        "provider": {
            "@type": "Organization",
            "name": "Oengines Games LLP",
            "url": "https://www.oengines.com"
        },
        "serviceType": "Cross-Platform App Development",
        "areaServed": "Worldwide",
        "description": "Cross-platform app development services for gaming products, game companion apps, fantasy sports, esports, tournaments, player communities, rewards, Android, and iOS.",
        "offers": services.map((service) => ({
            "@type": "Offer",
            "itemOffered": {
                "@type": "Service",
                "name": service.title,
                "description": service.description
            }
        }))
    };

    return (
        <>
            <Helmet>
                <title>Cross-Platform App Development for Gaming Products | Oengines</title>
                <meta name="description" content="Oengines Games LLP builds cross-platform gaming apps for Android and iOS, including game companion apps, esports apps, fantasy sports, tournaments, wallets, rewards, leaderboards, and LiveOps." />
                <meta name="keywords" content="cross platform app development, gaming app development, Flutter app development, React Native app development, game companion app, esports app development, fantasy sports app development, tournament app development" />
                <link rel="canonical" href="https://www.oengines.com/cross-platform-apps" />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
            </Helmet>
            <Header />
            <main className="cross-app-page">
                <section className="cross-app-hero">
                    <div className="container">
                        <div className="row align-items-center g-4">
                            <div className="col-lg-7">
                                <span className="cross-app-eyebrow">Cross-Platform Apps for Gaming</span>
                                <h1>Build Gaming Apps for Android & iOS with One Product Strategy</h1>
                                <p>Oengines Games LLP builds cross-platform apps for game ecosystems, fantasy sports, esports, tournaments, player communities, wallets, rewards, leaderboards, and LiveOps support.</p>
                                <div className="cross-app-actions">
                                    <a href="/contact" className="as-btn">Start a Project <i className="fa-regular fa-arrow-right ms-2"></i></a>
                                    <a href="tel:+912614054978" className="cross-app-call">Call: (0261) - 4054978</a>
                                </div>
                                <div className="cross-app-hero-stats">
                                    <div><strong>Android</strong><span>Build Support</span></div>
                                    <div><strong>iOS</strong><span>Build Support</span></div>
                                    <div><strong>LiveOps</strong><span>Game Ready</span></div>
                                </div>
                            </div>
                            <div className="col-lg-5">
                                <div className="cross-app-hero-card">
                                    <img src="assets/img/GameDevelopment/flutter-header-right.png" alt="Cross-platform gaming app development" />
                                    <div>
                                        <strong>Shared App Delivery</strong>
                                        <span>Flutter, React Native, backend, wallets, rewards, tournaments, analytics, and publishing.</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="cross-app-section space">
                    <div className="container">
                        <div className="cross-app-heading text-center">
                            <span>Cross-Platform Services</span>
                            <h2>Gaming-Focused Cross-Platform App Development Services</h2>
                            <p>Build one app product experience for Android and iOS while keeping game-specific features, backend systems, and player engagement in focus.</p>
                        </div>
                        <div className="row g-4">
                            {services.map((service) => (
                                <div className="col-lg-3 col-md-6" key={service.title}>
                                    <article className="cross-app-card">
                                        <h3>{service.title}</h3>
                                        <p>{service.description}</p>
                                        <ul>
                                            {service.points.map((point) => <li key={point}>{point}</li>)}
                                        </ul>
                                    </article>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="cross-app-module-section space">
                    <div className="container">
                        <div className="cross-app-heading text-center">
                            <span>Gaming Modules</span>
                            <h2>Feature Modules for Gaming App Products</h2>
                            <p>These modules can support game companion apps, tournament products, fantasy sports, esports platforms, and player community apps.</p>
                        </div>
                        <div className="cross-app-module-grid">
                            {gamingModules.map((module) => (
                                <article className="cross-app-module-card" key={module.title}>
                                    <h3>{module.title}</h3>
                                    <p>{module.description}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="cross-app-tech-section space">
                    <div className="container">
                        <div className="cross-app-tech-panel">
                            <div className="cross-app-heading mb-0">
                                <span>Technology & Integrations</span>
                                <h2>Full Stack Coverage for Cross-Platform Gaming Apps</h2>
                                <p>We combine mobile frameworks, backend systems, analytics, admin tools, and publishing workflows to support launch and ongoing operations.</p>
                            </div>
                            <div className="cross-app-tech-list">
                                {technologies.map((item) => <span key={item}>{item}</span>)}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="cross-app-types-section space">
                    <div className="container">
                        <div className="cross-app-heading text-center">
                            <span>App Types</span>
                            <h2>Gaming App Concepts We Can Build</h2>
                        </div>
                        <div className="cross-app-types-list">
                            {appTypes.map((item) => <span key={item}>{item}</span>)}
                        </div>
                    </div>
                </section>

                <section className="cross-app-process-section space">
                    <div className="container">
                        <div className="cross-app-heading text-center">
                            <span>Development Process</span>
                            <h2>From Gaming App Concept to Android & iOS Launch</h2>
                        </div>
                        <div className="cross-app-process-grid">
                            {process.map((item) => (
                                <article className="cross-app-process-card" key={item.step}>
                                    <span>{item.step}</span>
                                    <h3>{item.title}</h3>
                                    <p>{item.description}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="cross-app-benefit-section space">
                    <div className="container">
                        <div className="cross-app-heading text-center">
                            <span>Why Choose Oengines</span>
                            <h2>Cross-Platform Apps Backed by Game Product Experience</h2>
                        </div>
                        <div className="row g-4">
                            {benefits.map((item) => (
                                <div className="col-lg-3 col-md-6" key={item.title}>
                                    <article className="cross-app-benefit-card">
                                        <span><i className={item.icon}></i></span>
                                        <h3>{item.title}</h3>
                                        <p>{item.description}</p>
                                    </article>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="cross-app-faq-section space">
                    <div className="container">
                        <div className="cross-app-heading text-center">
                            <span>FAQ</span>
                            <h2>Cross-Platform Gaming App Questions</h2>
                        </div>
                        <div className="cross-app-faq-grid">
                            {faqs.map((item) => (
                                <article className="cross-app-faq-card" key={item.question}>
                                    <h3>{item.question}</h3>
                                    <p>{item.answer}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="cross-app-cta">
                    <div className="container">
                        <div className="cross-app-cta-box">
                            <div>
                                <span>Build Once, Launch Wider</span>
                                <h2>Plan your cross-platform gaming app with Oengines.</h2>
                                <p>Share your game ecosystem, target platforms, backend needs, and launch goals. We will help define the right Android and iOS delivery path.</p>
                            </div>
                            <a href="/contact" className="as-btn">Book Consultation <i className="fa-regular fa-arrow-right ms-2"></i></a>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
